/**
 * Field-list builders for the generic status detail panel — turns a raw
 * status packet into the ordered label/value (or label/value/color) rows
 * the default and data-report renderers map over. Kept JSX-free so the
 * field derivations are unit-testable without a bundler.
 *
 * Contains:
 * - buildDeviceInfoFields — the "Device Info" LabeledValue row list
 * - buildNetworkDetailFields — the "Network Details" LabeledValue row list
 * - buildReportSummaryStats — the DATA_REPORT_STATUS summary StatMiniCard list
 * - buildAlarmColorStats — the ALARM_NOTIFICATION_V2 RGB StatMiniCard list
 */

function formatUptime(seconds) {
  if (seconds == null || seconds < 0) return '—';

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
}

function getDayName(dayOfWeek, t) {
  const dayNames = [
    t('general.unknown'),
    t('general.monday'),
    t('general.tuesday'),
    t('general.wednesday'),
    t('general.thursday'),
    t('general.friday'),
    t('general.saturday'),
    t('general.sunday'),
  ];
  return dayNames[dayOfWeek] ?? dayOfWeek;
}

/**
 * @param {object} data - the status packet's `data` payload.
 * @param {object} selectedItem - the selected status row (iccid/mac/firmware fields).
 * @param {Function} t - i18next translate function.
 * @returns {{label: string, value: *}[]} rows to render, in display order.
 */
export function buildDeviceInfoFields(data, selectedItem, t) {
  const { iccid, ip_address, mac_address, major, minor, patch } = selectedItem;
  const firmwareVersion = major != null && minor != null && patch != null ? `${major}.${minor}.${patch}` : '—';
  const signalStrength = data.rssi ? `-${data.rssi} dBm` : '—';

  return [
    (major || minor || patch) && { label: t('status.firmwareVersion'), value: firmwareVersion },
    { label: t('status.iccidMac'), value: iccid || mac_address || '—' },
    (ip_address || data.listen_port) && {
      label: t('status.ipAddressPort'),
      value: `${ip_address || '—'}:${data.listen_port || '—'}`,
    },
    data.rssi && { label: t('status.signalStrength'), value: signalStrength },
    data.uptime != null && { label: t('status.uptime'), value: formatUptime(data.uptime) },
    data.check_in_time_minutes != null && {
      label: t('status.checkInAfterMidnight'),
      value: `${data.check_in_time_minutes} ${t('general.minutes')}`,
    },
    data.on_delay != null && { label: t('status.onDelay'), value: `${data.on_delay} ${t('general.seconds')}` },
    data.sanity_rate != null && { label: t('status.sanityRate'), value: data.sanity_rate },
    data.day_of_week != null && { label: t('general.dayOfWeek'), value: getDayName(data.day_of_week, t) },
  ].filter(Boolean);
}

/**
 * @param {object} data - the status packet's `data` payload.
 * @param {Function} t - i18next translate function.
 * @returns {{label: string, value: *}[]} rows to render, in display order.
 */
export function buildNetworkDetailFields(data, t) {
  return [
    data.ip_address && { label: t('status.serverIp'), value: data.ip_address },
    data.lop_port != null && { label: t('status.lopPort'), value: data.lop_port },
    data.msg_port != null && { label: t('status.messagePort'), value: data.msg_port },
  ].filter(Boolean);
}

/**
 * @param {object} counts
 * @param {number} counts.colorSensorCount
 * @param {number} counts.tempSensorCount
 * @param {number} counts.intrusionSensorCount
 * @param {number} counts.nonCommonSensorCount
 * @param {Function} t - i18next translate function.
 * @returns {{label: string, value: number, color: string}[]} stat tiles, in display order.
 */
export function buildReportSummaryStats(
  { colorSensorCount, tempSensorCount, intrusionSensorCount, nonCommonSensorCount },
  t,
) {
  return [
    { label: t('status.colorSensors'), value: colorSensorCount, color: 'primary.main' },
    { label: t('status.temperatureSensors'), value: tempSensorCount, color: 'info.main' },
    { label: t('status.intrusionSensors'), value: intrusionSensorCount, color: 'warning.main' },
    { label: t('status.totalSensors'), value: nonCommonSensorCount, color: 'success.main' },
  ];
}

/**
 * @param {object} alarmData - the alarm notification's `data.data` sub-object.
 * @param {Function} t - i18next translate function.
 * @returns {{label: string, value: number, color: string}[]} stat tiles present in the payload.
 */
export function buildAlarmColorStats(alarmData, t) {
  return [
    alarmData.red !== undefined && { label: t('status.colorRed'), value: alarmData.red, color: 'error.main' },
    alarmData.green !== undefined && { label: t('status.colorGreen'), value: alarmData.green, color: 'success.main' },
    alarmData.blue !== undefined && { label: t('status.colorBlue'), value: alarmData.blue, color: 'primary.main' },
  ].filter(Boolean);
}
